import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { theme } from '../styles/theme';

const GlobalFont = createGlobalStyle`
  @font-face {
    font-family: 'Rubik Bubbles';
    src: url('../assets/fonts/RubikBubbles-Regular.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: 'Trispace';
    src: url('../assets/fonts/Trispace-Bold.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
  }
`;

// Animation for the gradient background
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Animation for floating particles
const float = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(10deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(-45deg, #1a472a, #2d5a3a, #1e8449, #145a32, #196f3d);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 15s ease infinite;
  color: #fff1d4;
  padding: ${theme.spacing[8]} ${theme.spacing[4]};
  overflow: hidden; /* Prevent horizontal overflow */
  width: 100%;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[6]} ${theme.spacing[4]};
    text-align: center;
  }
`;

const Particle = styled.div<{ size: number; left: number; animationDuration: number; delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background-image: url('/images/cannabis-leaf.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  left: ${props => props.left}%;
  top: -10%;
  opacity: 0.15;
  animation: ${float} ${props => props.animationDuration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  z-index: 1;
  filter: brightness(1.5);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 ${theme.spacing[4]};
  position: relative;
  z-index: 2;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[8]};
  min-height: 60vh;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex-direction: column;
    gap: ${theme.spacing[6]};
    text-align: center;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  max-width: 500px;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    max-width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  max-width: 500px;
  display: flex;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    max-width: 100%;
  }
`;

const Headline = styled.h1`
  font-size: calc(${theme.typography.fontSize['5xl']} * 1.2);
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing[6]};
  line-height: 1.3;
  text-align: left;
  font-family: 'Comfortaa', system-ui;
  letter-spacing: 2px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: #fff1d4;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    text-align: center;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['5xl']};
    letter-spacing: 1.5px;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize['4xl']};
    letter-spacing: 1px;
  }
`;

const Subheading = styled.p`
  font-size: ${theme.typography.fontSize['2xl']};
  margin-bottom: ${theme.spacing[8]};
  line-height: 1.5;
  text-align: left;
  font-family: 'Trispace', sans-serif;
  color: #fff1d4;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    text-align: center;
    margin-bottom: ${theme.spacing[6]};
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.xl};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const glowEffect = keyframes`
  0% { box-shadow: 0 0 5px rgba(76, 175, 80, 0.7), 0 0 10px rgba(76, 175, 80, 0.5); }
  50% { box-shadow: 0 0 15px rgba(76, 175, 80, 0.9), 0 0 20px rgba(76, 175, 80, 0.7); }
  100% { box-shadow: 0 0 5px rgba(76, 175, 80, 0.7), 0 0 10px rgba(76, 175, 80, 0.5); }
`;

const pulseEffect = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const rotateHue = keyframes`
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
`;

const rotateSpin = keyframes`
  0% { transform: translateX(-50%) rotate(0deg); }
  100% { transform: translateX(-50%) rotate(360deg); }
`;

const breathe = keyframes`
  0% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.05); }
  100% { transform: translateX(-50%) scale(1); }
`;

const rainbowGlow = keyframes`
  0% { box-shadow: 0 0 15px rgba(255, 0, 0, 0.7), 0 0 25px rgba(255, 0, 0, 0.5); }
  20% { box-shadow: 0 0 15px rgba(255, 165, 0, 0.7), 0 0 25px rgba(255, 165, 0, 0.5); }
  40% { box-shadow: 0 0 15px rgba(255, 255, 0, 0.7), 0 0 25px rgba(255, 255, 0, 0.5); }
  60% { box-shadow: 0 0 15px rgba(0, 128, 0, 0.7), 0 0 25px rgba(0, 128, 0, 0.5); }
  80% { box-shadow: 0 0 15px rgba(0, 0, 255, 0.7), 0 0 25px rgba(0, 0, 255, 0.5); }
  100% { box-shadow: 0 0 15px rgba(128, 0, 128, 0.7), 0 0 25px rgba(128, 0, 128, 0.5); }
`;

const logoGradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const PathOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const PathOptionsHeading = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  margin-bottom: ${theme.spacing[4]};
  text-align: center;
  background: linear-gradient(45deg, #4CAF50, #8BC34A, #CDDC39);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${rotateHue} 8s infinite linear;
  text-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: ${theme.spacing[6]};
  width: 100%;
  max-width: 700px;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing[4]};
  }
`;

const PathButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 220px;
  height: 180px;
  background: ${props => props.variant === 'secondary' ? '#1e7354' : '#fff1d4'} !important;
  border: 2px solid #4CAF50;
  border-radius: 12px;
  padding: ${theme.spacing[4]};
  color: ${props => props.variant === 'secondary' ? '#fff1d4' : '#4CAF50'};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${glowEffect} 3s infinite ease-in-out;
  z-index: 1000;
  
  &:hover {
    background-color: ${props => props.variant === 'secondary' ? '#145a32' : '#1e7354'};
    color: ${props => props.variant === 'secondary' ? '#ffffff' : '#fff1d4'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    width: 180px;
    height: 150px;
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const PathIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 36px;
    
    img {
      width: 36px;
      height: 36px;
    }
  }
`;

const PathText = styled.span`
  text-align: center;
  line-height: 1.2;
  color: inherit;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[4]};
  animation: ${breathe} 6s ease-in-out infinite;
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[3]};
    top: ${theme.spacing[6]};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[2]};
    top: ${theme.spacing[4]};
  }
`;

const logoHueRotate = keyframes`
  0% { filter: hue-rotate(0deg) drop-shadow(0 0 10px rgba(255, 105, 180, 0.8)); }
  25% { filter: hue-rotate(90deg) drop-shadow(0 0 12px rgba(50, 205, 50, 0.8)); }
  50% { filter: hue-rotate(180deg) drop-shadow(0 0 15px rgba(75, 0, 130, 0.8)); }
  75% { filter: hue-rotate(270deg) drop-shadow(0 0 12px rgba(255, 215, 0, 0.8)); }
  100% { filter: hue-rotate(360deg) drop-shadow(0 0 10px rgba(255, 105, 180, 0.8)); }
`;

const warping = keyframes`
  0% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.05) rotate(2deg); }
  50% { transform: scale(1) rotate(0deg); }
  75% { transform: scale(1.05) rotate(-2deg); }
  100% { transform: scale(1) rotate(0deg); }
`;

const Logo = styled.img`
  width: 150px;
  height: auto;
  animation: ${logoHueRotate} 8s infinite linear, ${warping} 6s infinite ease-in-out;
  transform-origin: center center;
  
  @media (max-width: ${theme.breakpoints.md}) {
    width: 100px;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 80px;
  }
`;

const floatingLogo = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(15px, -20px) rotate(10deg); }
  50% { transform: translate(30px, 15px) rotate(20deg); }
  75% { transform: translate(-10px, 25px) rotate(10deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const FloatingLogo = styled.img<{ size: number; top: number; left: number; delay: number; duration: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  opacity: 0.4;
  z-index: 1;
  animation: ${floatingLogo} ${props => props.duration}s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  filter: hue-rotate(${props => (props.delay * 40)}deg) drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
  pointer-events: none;
`;

const Hero: React.FC = () => {
  const scrollToNextSection = () => {
    const nextSection = document.getElementById('about') || document.getElementById('features');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [particles, setParticles] = useState<Array<{ size: number; left: number; animationDuration: number; delay: number }>>([]);
  const [floatingLogos, setFloatingLogos] = useState<Array<{ size: number; top: number; left: number; delay: number; duration: number }>>([]);

  useEffect(() => {
    // Generate random particles
    const newParticles = [];
    const particleCount = window.innerWidth < 768 ? 10 : 20;
    
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        size: Math.random() * 30 + 20,
        left: Math.random() * 100,
        animationDuration: Math.random() * 10 + 10,
        delay: Math.random() * 5
      });
    }
    
    setParticles(newParticles);
    
    // Generate floating logos
    const newFloatingLogos = [];
    
    // Determine logo counts based on screen size
    const isSmallScreen = window.innerWidth < 768;
    const topLogoCount = isSmallScreen ? 5 : 10;
    const middleLogoCount = isSmallScreen ? 4 : 8;
    const bottomLogoCount = isSmallScreen ? 5 : 10;
    
    // Add logos to the top portion (top 20%)
    for (let i = 0; i < topLogoCount; i++) {
      newFloatingLogos.push({
        size: Math.random() * 50 + 25, // Random size between 25-75px
        top: Math.random() * 15 + 5,   // Position in top 20% (5-20%)
        left: Math.random() * 90 + 5,  // Spread across width
        delay: Math.random() * 5,      
        duration: Math.random() * 10 + 15
      });
    }
    
    // Add logos to the middle portion - avoid button area
    for (let i = 0; i < middleLogoCount; i++) {
      let top, left;
      
      // Avoid the right side where buttons are located (especially on larger screens)
      if (window.innerWidth >= 1024) {
        // On large screens, buttons are on the right side, so avoid right 50%
        left = Math.random() * 40 + 5; // Position in left 45% (5-45%)
        top = Math.random() * 60 + 20;
      } else {
        // On smaller screens, buttons are centered, avoid center area
        const side = Math.random() > 0.5;
        if (side) {
          left = Math.random() * 25 + 5;  // Left side (5-30%)
        } else {
          left = Math.random() * 25 + 70; // Right side (70-95%)
        }
        top = Math.random() * 40 + 20; // Avoid center vertically too (20-60%)
      }
      
      newFloatingLogos.push({
        size: Math.random() * 60 + 30,
        top,
        left,
        delay: Math.random() * 5,
        duration: Math.random() * 10 + 15
      });
    }
    
    // Add logos to the bottom portion (bottom 20%)
    for (let i = 0; i < bottomLogoCount; i++) {
      newFloatingLogos.push({
        size: Math.random() * 50 + 25, // Random size between 25-75px
        top: Math.random() * 15 + 80,  // Position in bottom 20% (80-95%)
        left: Math.random() * 90 + 5,  // Spread across width
        delay: Math.random() * 5,
        duration: Math.random() * 10 + 15
      });
    }
    
    setFloatingLogos(newFloatingLogos);
  }, []);

  return (
    <HeroSection id="hero">
      <GlobalFont />
      {particles.map((particle, index) => (
        <Particle 
          key={`particle-${index}`}
          size={particle.size}
          left={particle.left}
          animationDuration={particle.animationDuration}
          delay={particle.delay}
        />
      ))}
      {floatingLogos.map((logo, index) => (
        <FloatingLogo
          key={`float-logo-${index}`}
          src="/logo512.png"
          alt="Floating Logo"
          size={logo.size}
          top={logo.top}
          left={logo.left}
          delay={logo.delay}
          duration={logo.duration}
        />
      ))}
      <HeroContent>
        <ContentWrapper>
          <LeftColumn>
            <LogoContainer>
              <Logo src="/logo512.png" alt="Green Growers Club Logo" />
            </LogoContainer>
            <Headline>The Conscious Cannabis Cloud</Headline>
            <Subheading>
              Empowering <span style={{fontWeight: 'bold', textDecoration: 'underline' }}>Ethical, Sustainable Growers</span> Through A Connected Ecosystem.
            </Subheading>
          </LeftColumn>
          <RightColumn>
            <PathOptionsContainer>
              <PathOptionsHeading>Choose Your Path</PathOptionsHeading>
              <ButtonsContainer>
                <PathButton onClick={scrollToNextSection}>
                  <PathIcon>
                    <img src="/logo512.png" alt="Green Growers Club Logo" />
                  </PathIcon>
                  <PathText>Cannabis Grower</PathText>
                </PathButton>
                <PathButton onClick={() => window.open('https://greent.club', '_self')} variant="secondary">
                  <PathIcon>
                    <img src="/images/greent-logo.png" alt="Greent Club Logo" />
                  </PathIcon>
                  <PathText>Conscious Consumer</PathText>
                </PathButton>
              </ButtonsContainer>
            </PathOptionsContainer>
          </RightColumn>
        </ContentWrapper>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;

